import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

// A share's or a recording's name, edited in the page (byj-1wv.38): a
// dialog as the page's questions are (Confirm), its field a real input —
// Enter saves, Esc or a click outside cancels. Only what its owner may
// change is asked; the relay checks the rest.

/** The longest name the relay takes (server.maxTitle). */
export const maxTitle = 120;

type Editing = { heading: string; value: string; label?: string; resolve: (title: string | null) => void };

/**
 * `[edit, dialog]`: `edit(heading, value)` shows the name to change and
 * resolves to the new one, trimmed, or null when nothing was saved; `dialog`
 * is the element to render, nothing while none is open.
 */
export function useEditTitle(): [(heading: string, value: string, label?: string) => Promise<string | null>, ReactNode] {
  const [editing, setEditing] = useState<Editing | null>(null);
  // `label` names the field when it is not a name: the agent cap's amount
  // (byj-1wv.46)
  const edit = useCallback(
    (heading: string, value: string, label?: string) =>
      new Promise<string | null>((resolve) => setEditing({ heading, value, label, resolve })),
    [],
  );
  const done = useCallback(
    (title: string | null) => {
      editing?.resolve(title);
      setEditing(null);
    },
    [editing],
  );
  return [edit, editing ? <EditTitleDialog editing={editing} onDone={done} /> : null];
}

function EditTitleDialog({ editing, onDone }: { editing: Editing; onDone: (title: string | null) => void }) {
  const [value, setValue] = useState(editing.value);
  const input = useRef<HTMLInputElement>(null);
  const title = value.trim();
  const changed = title !== "" && title !== editing.value.trim();

  // Esc closes it alone, and Tab stays in it (components/Modal)
  const keys = useModalKeys(() => onDone(null));
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onDone(null)}>
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-title-heading"
        onKeyDown={keys}
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) onDone(title);
        }}
      >
        <h3 id="edit-title-heading">{editing.heading}</h3>
        <label className="field">
          {editing.label ?? t("recordings:titleLabel")}
          <input ref={input} value={value} maxLength={maxTitle} onChange={(e) => setValue(e.target.value)} />
        </label>
        <div className="row modal-actions">
          <button type="button" onClick={() => onDone(null)}>
            {t("common:cancel")}
          </button>
          <button type="submit" className="primary" disabled={!changed}>
            {t("common:save")}
          </button>
        </div>
      </form>
    </div>
  );
}
